import { useSearchParams } from 'next/navigation'
import * as React from 'react'

import { ScreenReaderOnly, TypeStyle } from '@syconium/little-miss-figgy'
// TODO: Fix this auto-generated suppression.
// eslint-disable-next-line rulesdir/require-responsive-images
import { ProgressiveAsset } from '@syconium/little-miss-figgy'

import { createPdpUrl } from '../../../app/_components/navigation/createPdpUrl'
import { NextLink } from '../../../app/_components/navigation/NextLink'
import { useTranslation } from '../../../app/_providers/TranslationProvider.client'
import {
  modalAnimationDuration,
  standardModalBodyOpenClassName,
  standardModalPortalClassName,
} from '../../../brunswick/styles/constants'
import { resizeShopifyImageSource } from '../../../lib/utils'
import { mergeContentfulRichDocuments } from '../../../lib/utils/contentful'
import { Product } from '../../../types/graphql'
import { FitModelStateContainer } from '../../pages/products/ProductPage/contexts/FitModelStateContainer'
import { CollectionLinks } from '../../pages/products/ProductPage/DetailsSection/CollectionLink'
import { ProductDetailsAccordionSection } from '../../pages/products/ProductPage/DetailsSection/ProductDetailsAccordionSection/ProductDetailsAccordionSection'
// TODO: Fix this auto-generated suppression.
// eslint-disable-next-line rulesdir/require-next-modal-react-modal-wrapper
import { NextModal } from '../NextModal'

import {
  Body,
  Content,
  FullDetailsButton,
  Header,
  ImageWrapper,
  ModalDismiss,
  Summary,
  TextWrapper,
  modalStyles,
} from './styles'

export interface ProductDetailsModalProps {
  closeModal: () => void
  isOpen: boolean
  product: {
    careInstructions: Product['careInstructions']
    fitModel?: Product['fitModel']
    fitModelsDescriptions: Product['fitModelsDescriptions']
    images?: Product['images']
    rawMaterials?: Product['rawMaterials']
    rtfFeatures?: Product['rtfFeatures']
    collectionLinks: Product['collectionLinks']
    rawFit?: Product['rawFit']
    exclusive?: Product['exclusive']
    colorInfo?: {
      rawName: NonNullable<Product['colorInfo']>['rawName']
    }
  }
  productGroup: {
    handle: string
    title: string
    rtfFeatures?: Product['productGroup']['rtfFeatures']
    genderCategory: Product['productGroup']['genderCategory']
  }
}

const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({
  closeModal,
  isOpen,
  product,
  productGroup,
}) => {
  const {
    detailsSection: {
      detailsModal: { title: modalTitle, viewFullDetails },
    },
  } = useTranslation()

  // plain=true query param is used by iOS app to view webpages within a webview without the website's header/footer
  const plainQueryParamValue = useSearchParams()?.get('plain') ?? undefined

  const {
    careInstructions,
    fitModel,
    fitModelsDescriptions,
    images,
    rawMaterials,
    rtfFeatures: productRtfFeatures,
    collectionLinks,
  } = product

  const href = createPdpUrl({
    productGroupHandle: productGroup.handle,
    collectionHandle: undefined,
    isKit: false,
    isExclusive: product.exclusive,
    genderKey: productGroup.genderCategory,
    colorKey: product.colorInfo?.rawName,
    fitKey: product.rawFit,
    sizeKey: undefined,
    query: { plain: plainQueryParamValue },
  })
  const image = images?.nodes[0]
  const imageSize = 520

  return (
    <FitModelStateContainer.Provider
      initialState={{
        fitModel: fitModel ?? undefined,
      }}
    >
      <NextModal
        bodyOpenClassName={standardModalBodyOpenClassName}
        closeTimeoutMS={modalAnimationDuration}
        isOpen={isOpen}
        onRequestClose={closeModal}
        portalClassName={standardModalPortalClassName}
        style={modalStyles}
      >
        <Body>
          <Header>
            <ScreenReaderOnly>{modalTitle}</ScreenReaderOnly>
            <ModalDismiss onClick={closeModal} />
          </Header>

          <Content>
            {image?.source && (
              <ImageWrapper>
                <ProgressiveAsset.Image
                  alt={productGroup.title}
                  src={resizeShopifyImageSource({
                    width: imageSize,
                    height: imageSize,
                    source: image.source,
                  })}
                />
              </ImageWrapper>
            )}
            <TextWrapper>
              <Summary>
                <TypeStyle.HeadlineSmall>{productGroup.title}</TypeStyle.HeadlineSmall>
                {href && (
                  <NextLink href={href}>
                    <FullDetailsButton>{viewFullDetails}</FullDetailsButton>
                  </NextLink>
                )}
              </Summary>
              <ProductDetailsAccordionSection
                careInstructions={careInstructions}
                fitModelsDescriptions={fitModelsDescriptions}
                features={mergeContentfulRichDocuments(
                  productGroup.rtfFeatures,
                  productRtfFeatures
                )}
                materials={rawMaterials}
              />
              <CollectionLinks links={collectionLinks?.nodes || []} />
            </TextWrapper>
          </Content>
        </Body>
      </NextModal>
    </FitModelStateContainer.Provider>
  )
}

export default ProductDetailsModal
